/**
 * Feature-gate registry — single source of truth for flag-gated routes/codes.
 *
 * The backend's /feature-flags/me response contains ONLY enabled flag keys.
 * Disabled or never-created flags are absent from the response. The frontend
 * therefore can't distinguish "this code is not flag-gated at all" from "the
 * admin gated this code and toggled it off". This registry resolves that
 * ambiguity: a code listed here is flag-gated; absence in /feature-flags/me
 * means hidden / access denied. A code NOT listed here is permission-only and
 * always visible.
 *
 * Add a row to `FEATURE_GATES` whenever you create a new feature flag for a
 * page. Map the navigation `code` (from /screens/navigation) — or a frontend
 * route path key — to the flag `key` an admin will set in /platform/feature-flags.
 *
 * Convention: when a navigation code matches the flag key one-to-one (e.g.
 * `experiments`), use the same string for both. The map shape stays explicit
 * for the cases where they differ (e.g. nav code `admin_audit_logs` vs flag key
 * `audit-logs`).
 */
interface GateEntry {
  key: string;
  shipped?: boolean;
}

export const FEATURE_GATES = {
  // Outer-sidebar group from /screens/navigation
  experiments: { key: 'experiments' },
  // Product picker — Item Setup card
  'item-setup': { key: 'item-setup' },
  // Meta Ads scheduled enrichment flow (catalog-operations → enrichments/meta/*)
  // shipped: true — flag retired, feature is fully live; remove entry + call sites when cleaning up
  meta_ads: { key: 'meta-ads', shipped: true },
  // Subsidiary Configuration group — Brand Preferences + Customer Constraints
  // shipped: true — flag retired, feature is fully live; remove entry + call sites when cleaning up
  subsidiary_configuration: { key: 'subsidiary-configuration', shipped: true },
  // Trial workflow — catalog import + content enrichment for TRIAL_POC subsidiaries
  // shipped: true — flag retired, feature is fully live; remove entry + call sites when cleaning up
  trial_flow: { key: 'trial-flow', shipped: true },
  // Google Ads catalog trigger — shows max-products selector in the modal; off = use total from API
  max_catalog_trigger: { key: 'max-catalog-trigger' },
  // Feed-vs-enriched compare grid — secondary view on the enrichment product
  // listings, offered via a Table/Compare toggle in the preview section header.
  // Inline UI only (no route of its own) — gate with useFeatureGate, not requireFeatureGate.
  enrichment_compare_grid: { key: 'enrichment-compare-grid' },
  // Unified enriched-product listing — replaces the four bespoke preview tables
  // (Google/Meta × sample/overview) with the catalog-operations products UI.
  // Inline UI only (no route of its own) — gate with useFeatureGate, not
  // requireFeatureGate. Off = the existing per-flow tables render unchanged.
  unified_enriched_table: { key: 'unified-enriched-table' },
  // Editable enrichment grid — the third "editable grid" view on the unified
  // enrichment product listing, where cells edit inline / in a modal and persist
  // via the attribute PATCH. Inline UI only (no route of its own) — gate with
  // useFeatureGate, not requireFeatureGate. Off = only the card/table views show.
  editable_cells: { key: 'editable-cells' },
  // Product Details attribute diff — pairs a dropped current attribute with the
  // optimized attribute that replaces it into one `replaced` row instead of a
  // separate Removed and Added row. Inline UI only (no route of its own) — gate
  // with useFeatureGate, not requireFeatureGate. Off = renames render as
  // removed + added, which is what ships today. Additionally inert until the
  // enrichment payload carries the `replaces` link (see ProductDetailAttribute).
  pair_attribute_replacements: { key: 'pair-attribute-replacements' },
  // Treatment-per-arm selection on the Experiment Strategy step — lets the
  // operator assign the enrichment schema each arm serves. Gated because the
  // experiment API isn't wired to persist the selection yet. Inline UI only (no
  // route of its own) — gate with useFeatureGate, not requireFeatureGate. Off =
  // the section is hidden and the arm-treatment validation is inert.
  experiment_treatment_per_arm: { key: 'experiment-treatment-per-arm' },
  // Trial 20-product cap — when on, the trial product limit shown across the
  // flow drops from 500 to 20 and catalog uploads are capped at 20 data rows.
  // Inline UI only (no route of its own) — gate with useFeatureGate, not
  // requireFeatureGate. Off = the default 500-product behaviour, no row limit.
  trial_max_20: { key: 'trial-max-20' },
  // Batch-detail debug panel (super-admin only) — a floating panel surfacing the
  // raw response IDs for troubleshooting. Inline UI only (no route of its own) —
  // gate with useFeatureGate, not requireFeatureGate.
  'debugger-int': { key: 'debugger-int' },
  // ── Feed Audit audit-run flags — uniform across ALL subsidiaries (no lily-*
  //    slug coupling). The one-audit-per-subsidiary limit has no flag: super admins
  //    and org admins bypass it by role (`useFeedAuditUnlimited`). ──
  // Lift the per-run 20-product cap — when on, an audit run may cover more than 20
  // products (both the Select & run picker and the Upload row count). Admins skip the
  // cap regardless. Inline UI only (no route of its own) — gate with useFeatureGate,
  // not requireFeatureGate.
  lily_bypass_audit_cap: { key: 'lily-bypass-audit-cap' },
  // Restricted uploads (top weight over the admin bypass) — when on, the file-upload
  // entry point + /feed-audit/upload path are disabled even for admins
  // (Select & run is unaffected). Inline UI only (no route of its own) — gate with
  // useFeatureGate, not requireFeatureGate.
  lily_restricted_uploads: { key: 'lily-restricted-uploads' },
  // Meta feed audit — when on, the audit list offers Meta (upload + Select & run)
  // alongside Google, and Meta runs render the Meta results sections. Inline UI only
  // (no route of its own) — gate with useFeatureGate, not requireFeatureGate.
  feed_audit_meta: { key: 'feed-audit-meta' },
  // Restart experiment — the "Restart" CTA on the experiment detail dashboard
  // that clones a run into a new experiment (+ optional arm-assignment upload).
  // Inline UI only (no route of its own) — gate with useFeatureGate, not
  // requireFeatureGate. Off = the button is hidden.
  'restart-experiment': { key: 'restart-experiment' },
  // Compare Delivery — the pre-enrichment vs enriched vs delivered side-by-side
  // view reached from a delivered Catalog Operations batch
  // (`/catalog-operations/$batchId/compare-delivery`). Route-gated with
  // requireFeatureGate AND the entry buttons on the batch detail page use
  // useFeatureGate. Off = buttons hidden, deep link → /access-denied. Gated
  // because the delivery pane depends on BE PR #627 (`products/lookup`).
  'compare-delivery': { key: 'compare-delivery' },
  // Google Ads OAuth connect flows — the "Sign in with Google" one-click
  // connect-mode option (no credentials, account picker) and the
  // interactive-OAuth sub-choice inside advance mode (mints the refresh
  // token via consent instead of pasting one). Inline UI only (no route of
  // its own) — gate with useFeatureGate, not requireFeatureGate. Off = only
  // the pre-existing Customer ID path and manual refresh-token entry show,
  // exactly as before this feature shipped. Resuming/discarding a
  // connection already stuck mid-flow stays available regardless of this
  // flag — it only gates starting a *new* one.
  'ads-oauth': { key: 'ads-oauth' },
  // Data Sharing — shareable Google Ads connection links (`/subsidiary/data-sharing`
  // panel; the public `/connector/:token` pages are gated server-side because
  // they can't read flags). Keyed by the `integrations_sharing` nav code so the
  // sidebar hides the backend nav item when the flag is off; the route calls
  // requireFeatureGate('integrations_sharing'). See docs/connector-share-link.md.
  integrations_sharing: { key: 'ads-share-link' },
  // Pre-Delivery Wizard — replaces the "Review delivery" side panel with a
  // 3-step flow (decision → pre-delivery preview → live progress), reached
  // from Catalog Operations "Review" and Content Enrichment "Confirm &
  // Publish". Route-gated with requireFeatureGate; the entry-point buttons
  // use useFeatureGate and fall back to today's side panel when off. See
  // docs/pre-delivery-wizard.md.
  'delivery-wizard': { key: 'delivery-wizard' },
  // Search Impact — merges the product detail page's Gap Analysis and Search
  // Descriptors tabs into one tab (stat cards, descriptor coverage, change ledger,
  // needs-review). Inline UI only (no route of its own) — gate with useFeatureGate,
  // not requireFeatureGate. Off = the two separate tabs render unchanged. See
  // docs/search-impact-tab.md.
  search_impact_ui: { key: 'search-impact-ui' },
  // Batch Details Funnel — verdict banner, funnel stages, outcomes, issues with
  // product drill-ins, and a "Run details" tab on the catalog-operations batch
  // detail page, all from `jobs/{id}/funnel`. Shown alongside the existing
  // pipeline cards. Inline UI only (no route of its own) — gate with
  // useFeatureGate, not requireFeatureGate. Off = page unchanged. See
  // docs/batch-details-funnel.md.
  batch_funnel: { key: 'batch-funnel' },
  // Failed batch review — the internal, cross-subsidiary daily review of failed
  // Catalog Operations batches at `/platform/batch-review` (super-admin only via
  // the /platform layout guard). Route-gated with requireFeatureGate('batch-review');
  // the hardcoded Platform rail entry uses useFeatureGate('batch-review'). The
  // identity screen row (`admin_batch_review`, child of `platform_admin`) is
  // aliased below so any nav renderer that keys on the screen code hides it too.
  // See docs/batch-review.md.
  'batch-review': { key: 'batch-review' },
  admin_batch_review: { key: 'batch-review' },
  // ----- catalog-v2 -----
  // New catalog screens on agentic-catalog-service, built in parallel with the
  // legacy Catalog Operations / Workflow Runs / Experiments screens. Gates the whole
  // `/catalog-v2/*` route tree (layout route beforeLoad). Off = nothing visible;
  // reviewers enable it locally. See docs/catalog-service.md.
  catalog_v2: { key: 'catalog-v2' },
  // ----- end catalog-v2 -----
} as const satisfies Record<string, GateEntry>;

/** Code keys that are flag-gated (and not yet shipped). Used for type narrowing. */
export type GatedCode = keyof typeof FEATURE_GATES;

/** True when a code is in the registry and still actively gated (not shipped). */
export function isFeatureGated(code: string): code is GatedCode {
  const entry = (FEATURE_GATES as Record<string, GateEntry>)[code];
  return !!entry && !entry.shipped;
}

/**
 * Returns the flag key admins set in /platform/feature-flags for a gated code,
 * or undefined when the code is unregistered or marked shipped.
 * All consumers (requireFeatureGate, useFeatureGate, nav-main, copilot-shell)
 * treat undefined as "ungated — always allow."
 */
export function flagKeyForCode(code: string): string | undefined {
  const entry = (FEATURE_GATES as Record<string, GateEntry>)[code];
  if (!entry || entry.shipped) return undefined;
  return entry.key;
}
